import { useEffect, useRef, useState } from "react";
import { FaCalendarCheck, FaWhatsapp } from "react-icons/fa";
import { GrFormView } from "react-icons/gr";
import { HiOutlinePhoneMissedCall } from "react-icons/hi";
import { SlCallOut } from "react-icons/sl";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Webcam from "react-webcam";
import LoaderSpiner from "../LoaderSpiner";
import styles from "./CreateAgentComponent.module.css";

const languages = [
  { value: "en-IN", name: "English (India)" },
  { value: "en-US", name: "English (United States)" },
  { value: "en-GB", name: "English (United Kingdom)" },
  { value: "hi", name: "Hindi" },
  { value: "ar", name: "Arabic" },
  { value: "ru", name: "Russian" },
];

const templateImages = [
  "/avatar1.jpg",
  "/avatar2.jpg",
  "/avatar3.jpg",
  "/avatar4.jpg",
  "/avatar5.png",
  "/avatar6",
  "/avatar7.webp",
  "/avatar8.webp",
  "/avatar9.webp",
];

const CreateOutBoundAgentComponent = () => {
  const navigate = useNavigate();
  const [avatarName, setAvatarName] = useState("");
  const [avatarPrefix, setAvatarPrefix] = useState("");
  const [avatarImage, setAvatarImage] = useState(null);
  const [language, setLanguage] = useState("");
  const [gender, setGender] = useState("");
  const [voice, setVoice] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [model, setModel] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [temperature, setTemperature] = useState(0.3);
  const [firstSpeaking, setFirstSpeaking] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [maxDuration, setMaxDuration] = useState("");
  const [timeExceedMessage, setTimeExceedMessage] = useState("");
  const [transcript, setTranscript] = useState(false);
  const [callRecording, setCallRecording] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [isLoadingNumbers, setIsLoadingNumbers] = useState(false);
  const [numberError, setNumberError] = useState("");
  const [voices, setVoices] = useState([]);
  const [isLoadingVoices, setIsLoadingVoices] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const [prompts, setPrompts] = useState([]);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const [promptError, setPromptError] = useState("");
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [isLoadingKnowledgeBases, setIsLoadingKnowledgeBases] = useState(false);
  const [knowledgeBaseError, setKnowledgeBaseError] = useState("");
  const [corpusId, setCorpusId] = useState("");
  const [corpora, setCorpora] = useState([]);
  const [isLoadingCorpora, setIsLoadingCorpora] = useState(false);
  const [corpusError, setCorpusError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showCalcomPopup, setShowCalcomPopup] = useState(false);
  const [calcomToken, setCalcomToken] = useState("");
  const [showCallTransferPopup, setShowCallTransferPopup] = useState(false);
  const [callTransferNumber, setCallTransferNumber] = useState("");
  const [showWhatsappPopup, setShowWhatsappPopup] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [showTemplatePopup, setShowTemplatePopup] = useState(false); // Added for template images
  const [mode, setMode] = useState("inbound");
  const [bookCalendar, setBookCalendar] = useState(false);
  const [whatsapp, setWhatsapp] = useState(false);
  const [callTransfer, setCallTransfer] = useState(false);
  const [corpusList, setCorpusList] = useState([]);

  const location = useLocation();
  const [showAvatarPanel, setShowAvatarPanel] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const webcamRef = useRef(null);

  const voicesPerPage = 5;

  // Fetch phone numbers
  useEffect(() => {
    const fetchNumbers = async () => {
      setIsLoadingNumbers(true);
      setNumberError("");
      try {
        const token = localStorage.getItem("token");
        const authDataString = localStorage.getItem("authData");
        const authData = authDataString ? JSON.parse(authDataString) : null;
        const vendorId = authData?.user?.vendorId;

        console.log("Auth Data:", authData);
        console.log("Vendor ID:", vendorId);

        if (!vendorId) {
          throw new Error("Vendor ID not found in authData");
        }
        const response = await fetch(
          `https://prod.toxboxai.com/api/numbers/get-numbers-by-user/${vendorId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch numbers");
        const data = await response.json();
        setPhoneNumbers(Array.isArray(data.numbers) ? data.numbers : []);
      } catch (err) {
        setNumberError("Failed to load phone numbers");
        setPhoneNumbers([]);
      } finally {
        setIsLoadingNumbers(false);
      }
    };
    fetchNumbers();
  }, []);

  // Fetch voices
  useEffect(() => {
    const fetchVoices = async () => {
      setIsLoadingVoices(true);
      setVoiceError("");
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          "https://prod.toxboxai.com/api/voice/get-voice-by-display-name",
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch voices");
        const data = await response.json();
        setVoices(Array.isArray(data) ? data : []);
      } catch (err) {
        setVoiceError("Failed to load voices");
        setVoices([]);
      } finally {
        setIsLoadingVoices(false);
      }
    };
    fetchVoices();
  }, []);

  // Fetch corpora
  useEffect(() => {
    const fetchCorpora = async () => {
      setIsLoadingCorpora(true);
      setCorpusError("");
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `https://prod.toxboxai.com/api/vendorknowledge/get-assigned-corpus/${localStorage.getItem(
            "userId"
          )}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch corpora");
        const data = await response.json();
        setCorpora(Array.isArray(data.results) ? data.results : []);
      } catch (err) {
        setCorpusError("Failed to load corpora");
        setCorpora([]);
      } finally {
        setIsLoadingCorpora(false);
      }
    };
    fetchCorpora();
  }, []);

  const apiUrl = "https://prod.toxboxai.com";
  console.log("API URL:", apiUrl);

  const productionApiUrl = process.env.REACT_APP_PRODUCTION_API_URL;
  console.log("Production API URL:", productionApiUrl);

  const handleFileUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      setAvatarImage(e.target.files[0]);
      setShowAvatarPanel(false);
    }
  };
  // Avatar select
  const handleAvatarSelect = (imgUrl) => {
    setAvatarImage(imgUrl);
    setShowAvatarPanel(false);
  };

  const capturePhoto = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setAvatarImage(imageSrc);
    setShowCamera(false);
  };
  const cancelCamera = () => {
    setShowCamera(false);
  };

  // Fetch prompts based on mode
  useEffect(() => {
    const fetchPrompts = async () => {
      const newMode = location.pathname.startsWith("/outbound")
        ? "outbound"
        : "inbound";
      console.log("Pathname:", location.pathname, "New Mode:", newMode);
      setMode(newMode);
      setIsLoadingPrompts(true);
      setPromptError("");
      try {
        const token = localStorage.getItem("token");
        const userId = localStorage.getItem("userId");
        const response = await fetch(
          `https://prod.toxboxai.com/api/participant/assign-prompts-by-salesperson/${userId}/outbound`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch prompts");
        const data = await response.json();
        setPrompts(Array.isArray(data.prompts) ? data.prompts : []);
      } catch (err) {
        setPromptError("Failed to load prompts");
        setPrompts([]);
      } finally {
        setIsLoadingPrompts(false);
      }
    };
    fetchPrompts();
  }, [location.pathname]);

  // Fetch knowledge bases
  useEffect(() => {
    const fetchKnowledgeBases = async () => {
      setIsLoadingKnowledgeBases(true);
      setKnowledgeBaseError("");
      try {
        const token = localStorage.getItem("token");
        const userId = localStorage.getItem("userId");
        const response = await fetch(
          `https://prod.toxboxai.com/api/knowledge-base/get-knowledgeBase-by-creator/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch knowledge bases");
        const data = await response.json();
        setKnowledgeBases(
          Array.isArray(data.knowledgeBases) ? data.knowledgeBases : []
        );
      } catch (err) {
        setKnowledgeBaseError("Failed to load knowledge bases");
        setKnowledgeBases([]);
      } finally {
        setIsLoadingKnowledgeBases(false);
      }
    };
    fetchKnowledgeBases();
  }, []);

  const handlePreview = () => {
    navigate("/playground", {
      state: {
        voice: voice,
        prompt: systemPrompt,
      },
    });
  };

  const handleTemplateImageSelect = (imageUrl) => {
    setAvatarImage(imageUrl);
    setShowTemplatePopup(false);
    toast.success("Template image selected!");
  };

  const handleAddCorpus = () => {
    setCorpusList([
      ...corpusList,
      {
        corpus_id: "",
        nameOverride: "",
        descriptionOverride: "",
        max_results: 5,
      },
    ]);
  };

  const handleCorpusChange = (index, field, value) => {
    const updatedCorpusList = [...corpusList];
    updatedCorpusList[index][field] = value;

    // Auto-generate nameOverride and descriptionOverride for specific corpora
    if (field === "corpus_id") {
      if (value === "gera") {
        updatedCorpusList[index].nameOverride = "searchGera";
        updatedCorpusList[index].descriptionOverride =
          "Search Gera-related property facts (location, budget, amenities, possession dates)";
      } else if (value === "codename") {
        updatedCorpusList[index].nameOverride = "SearchCodename";
        updatedCorpusList[index].descriptionOverride =
          "Search Codename-related property facts (location, budget, amenities, possession dates)";
      }
    }

    setCorpusList(updatedCorpusList);
  };

  const handleRemoveCorpus = (index) => {
    setCorpusList(corpusList.filter((_, i) => i !== index));
  };

  const handleVoiceSelect = (displayName) => {
    setVoice(displayName);
    toast.success(`Selected voice: ${displayName}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missingFields = [];
    if (!avatarName) missingFields.push("Avatar Name");
    if (!language) missingFields.push("Language");
    if (!gender) missingFields.push("Gender");
    if (!voice) missingFields.push("Voice");
    if (!phoneNumber) missingFields.push("Phone Number");
    if (!model) missingFields.push("Model");
    if (!systemPrompt) missingFields.push("System Prompt");
    if (temperature === "" || temperature === null || temperature === undefined)
      missingFields.push("Temperature");
    if (firstSpeaking && !welcomeMessage) missingFields.push("Welcome Message");
    if (!maxDuration) missingFields.push("Max Duration");
    if (!timeExceedMessage) missingFields.push("Time Exceed Message");
    if (missingFields.length > 0) {
      return;
    }
    let firstSpeakerSettings;
    if (firstSpeaking) {
      firstSpeakerSettings = { agent: { welcomeMessage } };
    } else {
      firstSpeakerSettings = { user: {} };
    }
    const payload = {
      avatarName: avatarPrefix ? `${avatarPrefix}-${avatarName}` : avatarName,
      avatarImage: avatarImage
        ? typeof avatarImage === "string"
          ? avatarImage
          : ""
        : "",
      language,
      gender,
      voice,
      phoneNumber,
      model,
      systemPrompt,
      // corpusList: corpusId
      //   ? [
      //       {
      //         corpus_id: corpusId,
      //         nameOverride: "",
      //         descriptionOverride: "",
      //         max_results: 5,
      //       },
      //     ]
      //   : [],
      corpusList,
      temperature,
      firstSpeaking,
      firstSpeakerSettings,
      maxDuration: maxDuration ? `${maxDuration}s` : "",
      timeExceedMessage,
      transcript,
      callRecording,
      type: "outbound",
      bookCalendar,
      whatsapp,
      whatsappNumber,
      callTransfer,
      callTransferNumber,
      calcomToken,
    };
    console.log("Payload to create agent:", payload);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        "https://prod.toxboxai.com/api/agent-creation/create",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || "Agent created successfully!");
      } else {
        toast.error(data.message || "Failed to create agent.");
      }

      navigate("/outbound");
    } catch (error) {
      toast.error(error.message || "Failed to create agent.");
    }
  };

  const modelOptions = [
    { label: "Ultimate", value: "fixie-ai/ultravox" },
    { label: "Advanced", value: "fixie-ai/ultravox" },
    { label: "Elite", value: "fixie-ai/ultravox" },
    { label: "Max", value: "fixie-ai/ultravox" },
  ];

  useEffect(() => {
    const audioElements = document.querySelectorAll("audio");
    const handlePlay = (event) => {
      audioElements.forEach((audio) => {
        if (audio !== event.target) {
          audio.pause();
          audio.currentTime = 0;
        }
      });
    };

    audioElements.forEach((audio) => {
      audio.addEventListener("play", handlePlay);
    });

    return () => {
      audioElements.forEach((audio) => {
        audio.removeEventListener("play", handlePlay);
      });
    };
  }, [voices, currentPage]);

  // Filter voices based on selected gender
  const filteredVoices = gender
    ? voices.filter(
      (v) =>
        v.gender?.toLowerCase() === gender?.toLowerCase() ||
        v.gender === "Unknown"
    )
    : voices;

  const indexOfLastVoice = currentPage * voicesPerPage;
  const indexOfFirstVoice = indexOfLastVoice - voicesPerPage;
  const currentVoices = filteredVoices.slice(
    indexOfFirstVoice,
    indexOfLastVoice
  );
  const totalPages = Math.ceil(filteredVoices.length / voicesPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const generateRandomPrefix = () => {
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    let result = "";
    for (let i = 0; i < 3; i++) {
      result += letters.charAt(Math.floor(Math.random() * letters.length));
    }
    return result;
  };

  const handleAvatarNameChange = (e) => {
    const name = e.target.value.replace(`${avatarPrefix}-`, "");
    if (!avatarPrefix && name) {
      setAvatarPrefix(generateRandomPrefix());
    }
    setAvatarName(name);
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.container}>
        <div className={styles.headerWrap}>
          <h1 className={styles.header}>Create Outbound Agent</h1>
        </div>
        <form className={styles.formCard} onSubmit={handleSubmit}>
          <div className={styles.gridWrap}>
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Basic Information</h2>
              <div className={styles.fieldGroup}>
                <div className={styles.fieldWrap}>
                  <label className={styles.label}>
                    Avatar Name
                    <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="Enter avatar name with Campaign name"
                    value={
                      avatarPrefix
                        ? `${avatarPrefix}-${avatarName}`
                        : avatarName
                    }
                    onChange={handleAvatarNameChange}
                  />
                </div>

                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Avatar Image</label>
                  <div className={styles.uploadWrap}>
                    <div
                      className={styles.uploadLabel}
                      onClick={() => setShowAvatarPanel(true)}
                    >
                      {avatarImage ? (
                        <img
                          src={
                            typeof avatarImage === "string"
                              ? avatarImage
                              : URL.createObjectURL(avatarImage)
                          }
                          alt="Avatar Preview"
                          className={styles.avatarPreview}
                        />
                      ) : (
                        <span className={styles.uploadIcon}>Upload Image</span>
                      )}
                    </div>
                  </div>

                  {/* Side Panel */}
                  {showAvatarPanel && (
                    <div
                      className={styles.sidePanelOverlay}
                      onClick={() => setShowAvatarPanel(false)}
                    >
                      <div
                        className={styles.sidePanel}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <h3 className={styles.sidePanelTitle}>
                          Choose an Avatar
                        </h3>

                        {/* Avatar Grid */}
                        <div className={styles.avatarGrid}>
                          {templateImages.map((img, index) => (
                            <img
                              key={index}
                              src={img}
                              alt={`avatar-${index}`}
                              className={styles.avatarOption}
                              onClick={() => handleAvatarSelect(img)}
                            />
                          ))}
                        </div>

                        {/* Upload Option */}
                        <label className={styles.uploadButton}>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleFileUpload}
                          />
                        </label>

                        {/* Camera Button */}
                        <button
                          className={styles.cameraButton}
                          onClick={() => setShowCamera(true)}
                        >
                          Capture with Camera
                        </button>

                        {/* Camera Modal */}
                        {showCamera && (
                          <div className={styles.cameraOverlay}>
                            <div className={styles.cameraContainer}>
                              <Webcam
                                audio={false}
                                ref={webcamRef}
                                screenshotFormat="image/jpeg"
                                className={styles.cameraFeed}
                              />
                              <div className={styles.cameraButtons}>
                                <button
                                  onClick={capturePhoto}
                                  className={styles.captureButton}
                                >
                                  Capture
                                </button>
                                <button
                                  onClick={cancelCamera}
                                  className={styles.cancelButton}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                        {/* Cancel */}
                        <button
                          className={styles.cancelButton}
                          onClick={() => setShowAvatarPanel(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Language</label>
                  <select
                    className={styles.input}
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                  >
                    <option value="">Select language</option>
                    {languages.map((lang) => (
                      <option key={lang.value} value={lang.value}>
                        {lang.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={styles.fieldWrap}>
                  <label className={styles.label}>
                    Phone Number
                    <span style={{ color: "red" }}>*</span>
                  </label>
                  <select
                    className={styles.input}
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    disabled={isLoadingNumbers || !!numberError}
                  >
                    <option value="">
                      {isLoadingNumbers
                        ? "Loading numbers..."
                        : numberError
                          ? numberError
                          : "Select phone number"}
                    </option>
                    {Array.isArray(phoneNumbers) &&
                      phoneNumbers.map((phoneObj) => (
                        <option key={phoneObj._id} value={phoneObj.phoneNumber}>
                          {phoneObj.phoneNumber}
                        </option>
                      ))}
                  </select>
                </div>
                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Gender</label>
                  <select
                    className={styles.input}
                    value={gender}
                    onChange={(e) => {
                      setGender(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
              </div>
            </div>
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>
                Voice Selection
                <span style={{ color: "red" }}>*</span>
              </h2>
              <div className={styles.voiceSelectionWrapper}>
                <div className={styles.voiceOverlay} id="voiceOverlay">
                  <div className={styles.waveOverlayCenter}>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
                {isLoadingVoices ? (
                  <LoaderSpiner />
                ) : voiceError ? (
                  <p className={styles.error}>{voiceError}</p>
                ) : (
                  <>
                    <table className={styles.voiceTable}>
                      <thead>
                        <tr>
                          <th>Display Name</th>
                          <th>Preview</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentVoices.length > 0 ? (
                          currentVoices.map((v) => (
                            <tr key={v._id}>
                              <td>{v.displayName}</td>
                              <td>
                                <audio
                                  controls
                                  className={styles.audioPreview}
                                  onPlay={() =>
                                    document
                                      .getElementById("voiceOverlay")
                                      ?.classList.add(styles.playing)
                                  }
                                  onPause={() =>
                                    document
                                      .getElementById("voiceOverlay")
                                      ?.classList.remove(styles.playing)
                                  }
                                >
                                  <source
                                    src={`${apiUrl}${v.previewUrl}`}
                                    type="audio/mpeg"
                                  />
                                  Your browser does not support the audio
                                  element.
                                </audio>
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className={styles.selectButton}
                                  onClick={() =>
                                    handleVoiceSelect(v.displayName)
                                  }
                                  disabled={voice === v.displayName}
                                >
                                  {voice === v.displayName
                                    ? "Selected"
                                    : "Select"}
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="3">
                              No voices match the selected gender.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    {totalPages > 1 && (
                      <div className={styles.pagination}>
                        <button
                          className={styles.paginationButton}
                          onClick={() => paginate(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Prev
                        </button>
                        <span className={styles.paginationDots}>...</span>
                        <button
                          className={`${styles.paginationButton} ${styles.activePage}`}
                        >
                          {currentPage}
                        </button>
                        <span className={styles.paginationDots}>...</span>
                        <button
                          className={styles.paginationButton}
                          onClick={() => paginate(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Agent Configuration</h2>
              <div className={styles.fieldGroup}>
                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Model</label>
                  <select
                    className={styles.input}
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                  >
                    <option value="">Select model</option>
                    {modelOptions.map((opt) => (
                      <option key={opt.label} value={opt.label}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={styles.fieldWrap}>
                  <label className={styles.label}>
                    System Prompt
                    <span style={{ color: "red" }}>*</span>
                  </label>
                  <select
                    className={styles.input}
                    value={systemPrompt}
                    onChange={(e) => setSystemPrompt(e.target.value)}
                    disabled={isLoadingPrompts || !!promptError}
                  >
                    <option value="">
                      {isLoadingPrompts
                        ? "Loading prompts..."
                        : promptError
                          ? promptError
                          : "Select system prompt"}
                    </option>
                    {Array.isArray(prompts) &&
                      prompts.map((p) => (
                        <option key={p._id} value={p._id}>
                          {p.promptName}
                        </option>
                      ))}
                  </select>
                </div>
                {/* <div className={styles.fieldWrap}>
                  <label className={styles.label}>
                    Corpus (Knowledge Base)
                  </label>
                  <select
                    className={styles.input}
                    value={corpusId}
                    onChange={(e) => setCorpusId(e.target.value)}
                    disabled={isLoadingCorpora || !!corpusError}
                  >
                    <option value="">
                      {isLoadingCorpora
                        ? "Loading corpora..."
                        : corpusError
                        ? corpusError
                        : "Select corpus (optional)"}
                    </option>
                    {Array.isArray(corpora) &&
                      corpora.map((corpus) => (
                        <option key={corpus.corpusId} value={corpus.corpusId}>
                          {corpus.name}
                        </option>
                      ))}
                  </select>
                </div> */}
                <div className={styles.fieldWrap}>
                  <label className={styles.label}>Knowledge Bases</label>
                  <button
                    type="button"
                    className={styles.submitBtn}
                    onClick={handleAddCorpus}
                  >
                    Add Knowledge Base
                  </button>
                  {corpusList.map((corpus, index) => (
                    <div key={index} className={styles.corpusItem}>
                      <h4>Knowledge Base {index + 1}</h4>
                      <select
                        className={styles.input}
                        value={corpus.corpus_id}
                        onChange={(e) =>
                          handleCorpusChange(index, "corpus_id", e.target.value)
                        }
                        disabled={isLoadingCorpora || !!corpusError}
                      >
                        <option value="">
                          {isLoadingCorpora
                            ? "Loading corpora..."
                            : corpusError
                              ? corpusError
                              : "Select corpus"}
                        </option>
                        {Array.isArray(corpora) &&
                          corpora.map((c) => (
                            <option key={c.corpusId} value={c.corpusId}>
                              {c.name}
                            </option>
                          ))}
                      </select>
                      <input
                        type="text"
                        className={styles.input}
                        placeholder="Name Override (e.g., searchGera)"
                        value={corpus.nameOverride}
                        onChange={(e) =>
                          handleCorpusChange(
                            index,
                            "nameOverride",
                            e.target.value
                          )
                        }
                      />
                      <textarea
                        className={styles.textarea}
                        rows={3}
                        placeholder="Description Override (e.g., Search Gera-related property facts)"
                        value={corpus.descriptionOverride}
                        onChange={(e) =>
                          handleCorpusChange(
                            index,
                            "descriptionOverride",
                            e.target.value
                          )
                        }
                      />
                      <input
                        type="number"
                        className={styles.input}
                        placeholder="Max Results (default 5)"
                        value={corpus.max_results}
                        onChange={(e) =>
                          handleCorpusChange(
                            index,
                            "max_results",
                            Number(e.target.value)
                          )
                        }
                        min="1"
                        max="20"
                      />
                      <button
                        type="button"
                        className={styles.cancelBtn}
                        onClick={() => handleRemoveCorpus(index)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                <div className={styles.fieldWrap}>
                  <label className={styles.label}>
                    Temperature (0.1 - 1.0)
                    <span style={{ color: "red" }}>*</span>
                    <span className={styles.tooltipContainer}>
                      <GrFormView className={styles.viewicon} />
                      <span className={styles.tooltipText}>
                        Temperature controls the randomness of responses.
                        <br />
                        Lower values (e.g., 0.1) make output more focused and
                        deterministic.
                        <br />
                        Higher values (e.g., 1.0) make output more creative and
                        diverse.
                      </span>
                    </span>
                  </label>
                  <div className={styles.sliderWrapper}>
                    <input
                      type="range"
                      min={0.1}
                      max={1.0}
                      step={0.1}
                      value={temperature}
                      onChange={(e) => setTemperature(Number(e.target.value))}
                      className={styles.slider}
                    />
                    <span className={styles.sliderValue}>{temperature}</span>
                  </div>
                </div>
                <div className={styles.toggleCard}>
                  <div className={styles.toggleLabelWrap}>
                    <span className={styles.label}>First Speaking:</span>
                    <div className={styles.toggleOptions}>
                      <span
                        className={
                          !firstSpeaking
                            ? styles.activeToggle
                            : styles.inactiveToggle
                        }
                      >
                        User
                      </span>
                      <span
                        className={
                          firstSpeaking
                            ? styles.activeToggle
                            : styles.inactiveToggle
                        }
                      >
                        Agent
                      </span>
                    </div>
                    <label className={styles.switch}>
                      <input
                        type="checkbox"
                        checked={firstSpeaking}
                        onChange={() => setFirstSpeaking(!firstSpeaking)}
                      />
                      <span className={styles.sliderSwitch}></span>
                    </label>
                  </div>

                  {firstSpeaking && (
                    <div className={styles.fieldWrap}>
                      <label className={styles.label}>Welcome Message</label>
                      <textarea
                        className={styles.textarea}
                        rows={4}
                        placeholder="Enter welcome message"
                        value={welcomeMessage}
                        onChange={(e) => setWelcomeMessage(e.target.value)}
                      />
                    </div>
                  )}
                </div>

                <div className={styles.fieldWrap}>
                  <label className={styles.label}>
                    <div className={styles.tooltipContainer}>
                      Max Duration (seconds)
                      <span style={{ color: "red" }}>*</span>
                      <span className={styles.tooltipText}>
                        Max Duration sets the time limit for the agent's
                        response.
                        <br />
                        It defines how long the AI can speak or generate output
                        <br />
                        (in seconds) before stopping.
                      </span>
                    </div>
                  </label>
                  <input
                    type="number"
                    className={styles.input}
                    placeholder="Enter max duration"
                    value={maxDuration}
                    onChange={(e) => setMaxDuration(e.target.value)}
                  />
                  <small className={styles.helperText}>
                    Duration will be sent as "Xs" (e.g., 30s)
                  </small>
                </div>
                <div className={styles.fieldWrap}>
                  <label className={styles.label}>
                    <div className={styles.tooltipContainer}>
                      Time Exceed Message
                      <span className={styles.tooltipText}>
                        Time limit exceeded. The agent stopped responding.
                      </span>
                    </div>
                  </label>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="Enter time exceed message"
                    value={timeExceedMessage}
                    onChange={(e) => setTimeExceedMessage(e.target.value)}
                  />
                </div>
                <div className={styles.toggleRow}>
                  <span
                    className={`${styles.label} ${styles.tooltipContainer}`}
                  >
                    Enable Transcript
                    <span className={styles.tooltipText}>
                      Enable Transcript allows you to view a text
                      <br />
                      version of the conversation between the AI
                      <br />
                      and the user.
                    </span>
                  </span>
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      checked={transcript}
                      onChange={() => setTranscript(!transcript)}
                    />
                    <span className={styles.sliderSwitch}></span>
                  </label>
                </div>
                <div className={styles.toggleRow}>
                  <span className={styles.label}>Enable Call Recording</span>
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      checked={callRecording}
                      onChange={() => setCallRecording(!callRecording)}
                    />
                    <span className={styles.sliderSwitch}></span>
                  </label>
                </div>
              </div>
            </div>
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Tools</h2>
              <div className={styles.toolGroup}>
                <button
                  className={styles.toolButton}
                  onClick={() => alert("End Call functionality triggered")}
                >
                  <span className={styles.toolIcon}>
                    <HiOutlinePhoneMissedCall />
                  </span>{" "}
                  End Call
                </button>
                <button
                  className={styles.toolButton}
                  onClick={() => setShowCalcomPopup(true)}
                >
                  <span className={styles.toolIcon}>
                    <FaCalendarCheck />
                  </span>{" "}
                  Book Calendar
                </button>
                <button
                  className={styles.toolButton}
                  onClick={() => setShowCallTransferPopup(true)}
                >
                  <span className={styles.toolIcon}>
                    <SlCallOut />
                  </span>{" "}
                  Call Transfer
                </button>
                <button
                  className={styles.toolButton}
                  onClick={() => setShowWhatsappPopup(true)}
                >
                  <span className={styles.toolIcon}>
                    <FaWhatsapp />
                  </span>{" "}
                  WhatsApp
                </button>
              </div>
              <div className={styles.toggleRow}>
                <span className={styles.label}>Enable Book Calendar</span>
                <label className={styles.switch}>
                  <input
                    type="checkbox"
                    checked={bookCalendar}
                    onChange={() => setBookCalendar(!bookCalendar)}
                  />
                  <span className={styles.sliderSwitch}></span>
                </label>
              </div>
              <div className={styles.toggleRow}>
                <span className={styles.label}>Enable WhatsApp</span>
                <label className={styles.switch}>
                  <input
                    type="checkbox"
                    checked={whatsapp}
                    onChange={() => setWhatsapp(!whatsapp)}
                  />
                  <span className={styles.sliderSwitch}></span>
                </label>
              </div>
              <div className={styles.toggleRow}>
                <span className={styles.label}>Enable Call Transfer</span>
                <label className={styles.switch}>
                  <input
                    type="checkbox"
                    checked={callTransfer}
                    onChange={() => setCallTransfer(!callTransfer)}
                  />
                  <span className={styles.sliderSwitch}></span>
                </label>
              </div>
              {showCalcomPopup && (
                <div
                  className={styles.popupOverlay}
                  onClick={() => setShowCalcomPopup(false)}
                >
                  <div
                    className={styles.popupContent}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h3 className={styles.popupTitle}>Enter Cal.com API Key</h3>
                    <input
                      type="text"
                      className={styles.input}
                      placeholder="Enter Cal.com API Key"
                      value={calcomToken}
                      onChange={(e) => setCalcomToken(e.target.value)}
                    />
                    <div className={styles.popupButtons}>
                      <button
                        className={styles.submitBtn}
                        onClick={() => {
                          alert(`Cal.com API Key saved: ${calcomToken}`);
                          setShowCalcomPopup(false);
                        }}
                      >
                        Save
                      </button>
                      <button
                        className={styles.cancelBtn}
                        onClick={() => setShowCalcomPopup(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {showCallTransferPopup && (
                <div
                  className={styles.popupOverlay}
                  onClick={() => setShowCallTransferPopup(false)}
                >
                  <div
                    className={styles.popupContent}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h3 className={styles.popupTitle}>
                      Enter Call Transfer Number
                    </h3>
                    <input
                      type="text"
                      className={styles.input}
                      placeholder="Enter phone number (e.g., +1234567890)"
                      value={callTransferNumber}
                      onChange={(e) => setCallTransferNumber(e.target.value)}
                    />
                    <div className={styles.popupButtons}>
                      <button
                        className={styles.submitBtn}
                        onClick={() => {
                          alert(
                            `Call Transfer Number saved: ${callTransferNumber}`
                          );
                          setShowCallTransferPopup(false);
                        }}
                      >
                        Save
                      </button>
                      <button
                        className={styles.cancelBtn}
                        onClick={() => setShowCallTransferPopup(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {showWhatsappPopup && (
                <div
                  className={styles.popupOverlay}
                  onClick={() => setShowWhatsappPopup(false)}
                >
                  <div
                    className={styles.popupContent}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h3 className={styles.popupTitle}>Enter WhatsApp Number</h3>
                    <input
                      type="text"
                      className={styles.input}
                      placeholder="Enter WhatsApp number (e.g., +1234567890)"
                      value={whatsappNumber}
                      onChange={(e) => setWhatsappNumber(e.target.value)}
                    />
                    <div className={styles.popupButtons}>
                      <button
                        className={styles.submitBtn}
                        onClick={() => {
                          alert(`WhatsApp Number saved: ${whatsappNumber}`);
                          setShowWhatsappPopup(false);
                        }}
                      >
                        Save
                      </button>
                      <button
                        className={styles.cancelBtn}
                        onClick={() => setShowWhatsappPopup(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {showTemplatePopup && (
                <div
                  className={styles.popupOverlay}
                  onClick={() => setShowTemplatePopup(false)}
                >
                  <div
                    className={styles.popupContent}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h3 className={styles.popupTitle}>Select Template Image</h3>
                    <div className={styles.templateImageGrid}>
                      {templateImages.map((image, index) => (
                        <img
                          key={index}
                          src={image}
                          alt={`Template ${index + 1}`}
                          className={styles.templateImage}
                          onClick={() => handleTemplateImageSelect(image)}
                        />
                      ))}
                    </div>
                    <div className={styles.popupButtons}>
                      <button
                        className={styles.cancelBtn}
                        onClick={() => setShowTemplatePopup(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className={styles.buttonRow}>
            <button
              type="button"
              className={styles.submitBtn}
              onClick={handlePreview}
            >
              Preview
            </button>
            <button type="submit" className={styles.submitBtn}>
              Create Outbound Agent
            </button>
          </div>
        </form>
        <ToastContainer />
      </div>
    </div>
  );
};

export default CreateOutBoundAgentComponent;
